import D from "@/admin/dashboard/Dashboard.module.css";

import { AdminCountryLocationPoolSelect, type HostScope } from "@/admin/dashboard/AdminCountryLocationPoolSelect.tsx";
import { BarChart } from "@/admin/dashboard/BarChart.tsx";
import { DonutChart, SmallDonut } from "@/admin/dashboard/DonutChart.tsx";
import type { LegendItem } from "@/admin/dashboard/LegendTable.tsx";
import { LegendTable } from "@/admin/dashboard/LegendTable.tsx";
import { LightBlock } from "@/admin/dashboard/LightBlock.tsx";
import { adminAllHostsQueryAtom, type AdminHostWithType } from "@/admin/hosts/hostQuery.ts";
import { BadgeDot } from "@/components/Badge.tsx";
import { Separator } from "@/components/Separator.tsx";
import { ErrorFallback } from "@/modules/error/ErrorFallback.tsx";
import { getPreferredLocation } from "@/modules/location/location.ts";
import { getIso3CountryInfo } from "@/modules/location/locationCountry.ts";
import { usePricingLocations } from "@/modules/location/locationQuery.ts";
import { filterFalse } from "@warrenio/utils/collections/filterFalse";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { useAtomValue } from "jotai/react";
import { useMemo, useState } from "react";

function SectionHeader({ hosts }: { hosts: Map<string, AdminHostWithType> }) {
    let availableCount = 0;

    let totalRam = 0;
    let totalRamUsed = 0;
    let totalRamFree = 0;
    let totalRamUnavailable = 0;

    let totalCpu = 0;
    let totalCpuUsed = 0;
    let totalCpuFree = 0;
    let totalCpuUnavailable = 0;

    for (const item of hosts.values()) {
        totalRamUsed += item.allocated_vmem;
        totalCpuUsed += item.allocated_vcpu;

        if (item.is_accepting_workloads && !item.is_maintenance && !item.is_offline) {
            availableCount++;
            totalRam += item.usable_vmem_amount;
            totalCpu += item.usable_vcpu_amount;

            totalRamFree += item.vmem_free > 0 ? item.vmem_free : 0;
            totalCpuFree += item.vcpu_free > 0 ? item.vcpu_free : 0;
        } else {
            totalRam += item.allocated_vmem;
            totalCpu += item.allocated_vcpu;

            totalRamUnavailable += item.vmem_free;
            totalCpuUnavailable += item.vcpu_free;
        }
    }

    const ramUsedPercent = (totalRamUsed / totalRam) * 100;
    const ramFreePercent = (totalRamFree / totalRam) * 100;
    const ramUnavailablePercent = (totalRamUnavailable / totalRam) * 100;

    const cpuUsedPercent = (totalCpuUsed / totalCpu) * 100;
    const cpuFreePercent = (totalCpuFree / totalCpu) * 100;
    const cpuUnavailablePercent = (totalCpuUnavailable / totalCpu) * 100;

    const ramTooltipItems = filterFalse<LegendItem>([
        { label: "vRAM allocated:", value: `${totalRamUsed.toFixed(0)} GB`, color: "color-chart-2" },
        { label: "vRAM available:", value: `${totalRamFree.toFixed(0)} GB`, color: "color-chart-free" },
        !!totalRamUnavailable && {
            label: "Not available:",
            value: `${totalRamUnavailable.toFixed(0)} GB`,
            color: "color-secondary",
        },
        {
            label: "Total:",
            value: `${(totalRamUsed + totalRamFree + totalRamUnavailable).toFixed(0)} GB`,
            color: "color-text",
        },
    ]);

    const cpuTooltipItems = filterFalse<LegendItem>([
        { label: "vCPU allocated:", value: totalCpuUsed.toFixed(0), color: "color-chart-3" },
        { label: "vCPU available:", value: totalCpuFree.toFixed(0), color: "color-chart-free" },
        !!totalCpuUnavailable && {
            label: "Not available:",
            value: totalCpuUnavailable.toFixed(0),
            color: "color-secondary",
        },
        { label: "Total:", value: (totalCpuUsed + totalCpuFree + totalCpuUnavailable).toFixed(0), color: "color-text" },
    ]);

    const ramItems = [
        { value: ramUsedPercent, color: "var(--color-chart-2)" },
        { value: ramFreePercent, color: "var(--color-chart-free)" },
        { value: ramUnavailablePercent, color: "var(--color-secondary)" },
    ];

    const cpuItems = [
        { value: cpuUsedPercent, color: "var(--color-chart-3)" },
        { value: cpuFreePercent, color: "var(--color-chart-free)" },
        { value: cpuUnavailablePercent, color: "var(--color-secondary)" },
    ];

    return (
        <div className={D.ItemHeader}>
            <div>
                <h2 className="font-size-heading">Compute Allocation</h2>
                <p>
                    Hypervisors ({availableCount}/{hosts.size})
                </p>
            </div>

            {(!!ramUsedPercent || !!ramFreePercent || !!ramUnavailablePercent) && (
                <SmallDonut
                    marginLeft
                    title="Total vRAM allocated"
                    items={ramItems}
                    tooltip={<LegendTable items={ramTooltipItems} />}
                    total={`${ramUsedPercent.toFixed(1)}%`}
                />
            )}

            {(!!cpuUsedPercent || !!cpuFreePercent || !!cpuUnavailablePercent) && (
                <SmallDonut
                    title="Total vCPU allocated"
                    items={cpuItems}
                    tooltip={<LegendTable items={cpuTooltipItems} />}
                    total={`${cpuUsedPercent.toFixed(1)}%`}
                />
            )}
        </div>
    );
}

function getScopeName(scope: HostScope) {
    switch (scope.type) {
        case "country":
            return scope.value.name;
        case "location":
            return scope.value.display_name;
        case "pool":
            return scope.value.name;
        default:
            return exhaustiveSwitchCheck(scope);
    }
}

function ComputeAllocationSectionContent() {
    const { error, data: hosts } = useAtomValue(adminAllHostsQueryAtom);
    const locations = usePricingLocations();
    const location = getPreferredLocation(locations);
    const country = getIso3CountryInfo(location.country_code);
    const [scope, setScope] = useState<HostScope>(
        country ? { type: "country", value: country } : { type: "location", value: location },
    );

    const scopeHosts = useMemo<AdminHostWithType[]>(() => {
        const hostPools = [...hosts.values()];
        switch (scope.type) {
            case "country": {
                const countrySlugs = new Set(
                    locations
                        .filter((item) => item.country_code.toLowerCase() === scope.value.code.iso3.toLowerCase())
                        .map((item) => item.slug),
                );
                return hostPools.filter((item) => countrySlugs.has(item.location));
            }
            case "location":
                return hostPools.filter((item) => item.location === scope.value.slug);
            case "pool":
                return hostPools.filter((item) => item.pool_uuid === scope.value.uuid);
            default:
                return exhaustiveSwitchCheck(scope);
        }
    }, [scope, hosts, locations]);

    const scopeAvailable = scopeHosts.filter(
        (item) => item.is_accepting_workloads && !item.is_maintenance && !item.is_offline,
    );

    /* scope specific */
    let totalRam_Loc = 0;
    let totalRamUsed_Loc = 0;
    let totalRamFree_Loc = 0;
    let totalRamUnavailable_Loc = 0;

    let totalCpu_Loc = 0;
    let totalCpuUsed_Loc = 0;
    let totalCpuFree_Loc = 0;
    let totalCpuUnavailable_Loc = 0;

    for (const item of scopeHosts) {
        totalRamUsed_Loc += item.allocated_vmem;
        totalCpuUsed_Loc += item.allocated_vcpu;

        if (item.is_accepting_workloads && !item.is_maintenance && !item.is_offline) {
            totalRam_Loc += item.usable_vmem_amount;
            totalRamFree_Loc += item.vmem_free > 0 ? item.vmem_free : 0;
            totalCpu_Loc += item.usable_vcpu_amount;
            totalCpuFree_Loc += item.vcpu_free > 0 ? item.vcpu_free : 0;
        } else {
            totalRamUnavailable_Loc += item.vmem_free;
            totalCpuUnavailable_Loc += item.vcpu_free;
            totalRam_Loc += item.allocated_vmem;
            totalCpu_Loc += item.allocated_vcpu;
        }
    }

    const ramUsedPercent_Loc = (totalRamUsed_Loc / totalRam_Loc) * 100;
    const ramFreePercent_Loc = (totalRamFree_Loc / totalRam_Loc) * 100;
    const ramUnavailablePercent_Loc = (totalRamUnavailable_Loc / totalRam_Loc) * 100;

    const cpuUsedPercent_Loc = (totalCpuUsed_Loc / totalCpu_Loc) * 100;
    const cpuFreePercent_Loc = (totalCpuFree_Loc / totalCpu_Loc) * 100;
    const cpuUnavailablePercent_Loc = (totalCpuUnavailable_Loc / totalCpu_Loc) * 100;

    const ramTooltipLocItems = filterFalse<LegendItem>([
        { label: "vRAM allocated:", value: `${totalRamUsed_Loc.toFixed(0)} GB`, color: "color-chart-2" },
        { label: "vRAM available:", value: `${totalRamFree_Loc.toFixed(0)} GB`, color: "color-chart-free" },
        !!totalRamUnavailable_Loc && {
            label: "Not available:",
            value: `${totalRamUnavailable_Loc.toFixed(0)} GB`,
            color: "color-secondary",
        },
        {
            label: "Total:",
            value: `${(totalRamUsed_Loc + totalRamFree_Loc + totalRamUnavailable_Loc).toFixed(0)} GB`,
            color: "color-text",
        },
    ]);

    const cpuTooltipLocItems = filterFalse<LegendItem>([
        { label: "vCPU allocated:", value: totalCpuUsed_Loc.toFixed(0), color: "color-chart-3" },
        { label: "vCPU available:", value: totalCpuFree_Loc.toFixed(0), color: "color-chart-free" },
        !!totalCpuUnavailable_Loc && {
            label: "Not available:",
            value: totalCpuUnavailable_Loc.toFixed(0),
            color: "color-secondary",
        },
        {
            label: "Total:",
            value: (totalCpuUsed_Loc + totalCpuFree_Loc + totalCpuUnavailable_Loc).toFixed(0),
            color: "color-text",
        },
    ]);

    const ramItemsLoc = [
        { value: ramUsedPercent_Loc, color: "var(--color-chart-2)" },
        { value: ramFreePercent_Loc, color: "var(--color-chart-free)" },
        { value: ramUnavailablePercent_Loc, color: "var(--color-secondary)" },
    ];

    const cpuItemsLoc = [
        { value: cpuUsedPercent_Loc, color: "var(--color-chart-3)" },
        { value: cpuFreePercent_Loc, color: "var(--color-chart-free)" },
        { value: cpuUnavailablePercent_Loc, color: "var(--color-secondary)" },
    ];

    return (
        <>
            <SectionHeader hosts={hosts} />

            <Separator />

            <div className={D.ItemLegend}>
                <div className="flex gap-1rem font-size-small">
                    <BadgeDot color="color-chart-2">vRAM allocated</BadgeDot>

                    <BadgeDot color="color-chart-free">vRAM / vCPU available</BadgeDot>

                    <BadgeDot color="color-chart-3">vCPU allocated</BadgeDot>

                    <BadgeDot color="color-secondary">Not available</BadgeDot>
                </div>

                <AdminCountryLocationPoolSelect onChange={setScope} />
            </div>

            <div className="flex items-center gap-1rem justify-between">
                <div>
                    <div className="font-size-title">{getScopeName(scope)}</div>
                    <p className="pb-3 font-size-small">
                        Hypervisors ({scopeAvailable.length}/{scopeHosts.length})
                    </p>
                </div>

                {!!ramUsedPercent_Loc && (
                    <div className="ml-auto flex items-center gap-0.5rem text-right">
                        <div className="font-size-small">vRAM ({ramUsedPercent_Loc.toFixed(1)}%)</div>

                        <div className="w-2rem">
                            <DonutChart items={ramItemsLoc} tooltip={<LegendTable items={ramTooltipLocItems} />} />
                        </div>
                    </div>
                )}

                {!!cpuUsedPercent_Loc && (
                    <div className="flex items-center gap-0.5rem text-right">
                        <div className="font-size-small">vCPU ({cpuUsedPercent_Loc.toFixed(1)}%)</div>
                        <div className="w-2rem">
                            <DonutChart items={cpuItemsLoc} tooltip={<LegendTable items={cpuTooltipLocItems} />} />
                        </div>
                    </div>
                )}
            </div>

            {scopeHosts.length ? (
                <BarChart items={scopeHosts} />
            ) : (
                error && (
                    <div className="h-83">
                        <ErrorFallback error={error} />
                    </div>
                )
            )}
        </>
    );
}

export function ComputeAllocationSection() {
    return (
        <LightBlock className={D.Wide} loadingHeight="460px">
            <ComputeAllocationSectionContent />
        </LightBlock>
    );
}
